import React, { useState } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spinner,
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { dataService } from '../../services/DataService';
import { useAppDispatch, useAppSelector } from '../reducer/reduxHooks';
import { fetchModules } from '../reducer/thunks';
import hashFileName from '../../utils/hashFileName';

interface CreateFormValues extends Record<string, any> {
  title: string;
  title_fr: string;
  title_it: string;
  description: string;
  description_fr: string;
  description_it: string;
  domain: string;
  order: number;
  status: string;
  imgSrc?: string;
  id?: string;
}

export default function ModuleCreateForm() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const modules = useAppSelector(
    (state) => state.moduleState.modules,
  ) as Array<Record<string, any>>;

  const [formValues, setFormValues] = useState<CreateFormValues>({
    title: '',
    title_fr: '',
    title_it: '',
    description: '',
    description_fr: '',
    description_it: '',
    domain: '',
    order: 0,
    status: 'draft',
  });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successId, setSuccessId] = useState<string | null>(null);
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  function slugify(base: string): string {
    return base
      .toLowerCase()
      .trim()
      .replace(/ä/g, 'ae')
      .replace(/ö/g, 'oe')
      .replace(/ü/g, 'ue')
      .replace(/ß/g, 'ss')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .substring(0, 60);
  }

  function getUniqueId(baseTitle: string): string {
    const base = slugify(baseTitle) || 'modul';
    let candidate = base;
    let counter = 2;
    const existingIds = new Set(modules.map((m) => m.id));
    while (existingIds.has(candidate)) {
      candidate = `${base}-${counter}`;
      counter += 1;
    }
    return candidate;
  }

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: name === 'order' ? Number(value) : value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessId(null);
    if (!formValues.title) {
      setError('Titel (DE) ist erforderlich');
      return;
    }
    setCreating(true);
    try {
      let imgSrcValue = formValues.imgSrc || '';
      if (selectedImageFile) {
        setUploadingImage(true);
        try {
          const hashedName = await hashFileName(selectedImageFile);
          const newIdTemp = getUniqueId(formValues.title);
          const uploadPath = `modules/${newIdTemp}/image/${hashedName}`;
          const uploadRes = await dataService.uploadFile(selectedImageFile, uploadPath);
          imgSrcValue = uploadRes.url;
        } catch (imgErr: any) {
          setError('Bild Upload fehlgeschlagen');
          setUploadingImage(false);
          setCreating(false);
          return;
        } finally {
          setUploadingImage(false);
        }
      }
      const newId = getUniqueId(formValues.title);
      const payload = {
        title: formValues.title || '',
        title_fr: formValues.title_fr || '',
        title_it: formValues.title_it || '',
        description: formValues.description || '',
        description_fr: formValues.description_fr || '',
        description_it: formValues.description_it || '',
        domain: formValues.domain || '',
        imgSrc: imgSrcValue,
        order: formValues.order || 0,
        status: formValues.status || 'draft',
        id: newId,
      };
      const res = await dataService.setItem('modules', newId, payload);
      if ([200, 201].includes(res.statusCode)) {
        setSuccessId(newId);
        fetchModules(dispatch);
        navigate(`/admin/modules/${newId}`, { state: { activeTab: 'base' } });
      } else {
        setError('Erstellen fehlgeschlagen');
      }
    } catch (err) {
      setError('Erstellen fehlgeschlagen');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Form onSubmit={handleSubmit} className="mt-3">
      {error && <div className="alert alert-danger py-2" role="alert">{error}</div>}
      {successId && (
        <div className="alert alert-success py-2" role="alert">
          <span>Modul erstellt:</span>
          <code>{successId}</code>
        </div>
      )}
      <Row className="g-3">
        <Col md={4}>
          <Form.Group controlId="createTitleDe">
            <Form.Label>Titel (DE) *</Form.Label>
            <Form.Control name="title" value={formValues.title} onChange={handleChange} required />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group controlId="createTitleFr">
            <Form.Label>Titel (FR)</Form.Label>
            <Form.Control name="title_fr" value={formValues.title_fr} onChange={handleChange} />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group controlId="createTitleIt">
            <Form.Label>Titel (IT)</Form.Label>
            <Form.Control name="title_it" value={formValues.title_it} onChange={handleChange} />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group controlId="createDomain" className="mt-2">
            <Form.Label>Domain</Form.Label>
            <Form.Control name="domain" value={formValues.domain} onChange={handleChange} />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group controlId="createOrder" className="mt-2">
            <Form.Label>Reihenfolge</Form.Label>
            <Form.Control type="number" name="order" value={formValues.order} onChange={handleChange} />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group controlId="createStatus" className="mt-2">
            <Form.Label>Status</Form.Label>
            <Form.Select name="status" value={formValues.status} onChange={handleChange}>
              <option value="draft">Entwurf</option>
              <option value="published">Veröffentlicht</option>
              <option value="archived">Archiviert</option>
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={12} className="mt-3">
          <Form.Group controlId="createImgSrc">
            <Form.Label>Modul Bild (imgSrc)</Form.Label>
            <Form.Control
              className="mb-2"
              name="imgSrc"
              placeholder="Optionale Remote URL"
              value={formValues.imgSrc || ''}
              onChange={handleChange}
            />
            <Form.Control
              type="file"
              accept="image/*"
              onChange={(ev: React.ChangeEvent<HTMLInputElement>) => {
                const { files } = ev.target;
                const file = files && files[0] ? files[0] : null;
                setSelectedImageFile(file);
                if (file) {
                  const objectUrl = URL.createObjectURL(file);
                  setImagePreview(objectUrl);
                } else {
                  setImagePreview(null);
                }
              }}
            />
            {imagePreview && (
              <div className="mt-2">
                <strong>Vorschau:</strong>
                <div><img src={imagePreview} alt="Bild Vorschau" style={{ maxWidth: '200px', height: 'auto' }} /></div>
              </div>
            )}
            {uploadingImage && (
              <div className="mt-2 small text-muted">Bild wird hochgeladen...</div>
            )}
            <div className="form-text">Alte Bilder werden nicht automatisch entfernt (Cleanup folgt).</div>
          </Form.Group>
        </Col>
        <Col md={12} className="mt-3">
          <Form.Group controlId="createDescriptionDe">
            <Form.Label>Beschreibung (DE)</Form.Label>
            <Form.Control as="textarea" rows={4} name="description" value={formValues.description} onChange={handleChange} />
          </Form.Group>
        </Col>
        <Col md={12} className="mt-3">
          <Form.Group controlId="createDescriptionFr">
            <Form.Label>Beschreibung (FR)</Form.Label>
            <Form.Control as="textarea" rows={4} name="description_fr" value={formValues.description_fr} onChange={handleChange} />
          </Form.Group>
        </Col>
        <Col md={12} className="mt-3">
          <Form.Group controlId="createDescriptionIt">
            <Form.Label>Beschreibung (IT)</Form.Label>
            <Form.Control as="textarea" rows={4} name="description_it" value={formValues.description_it} onChange={handleChange} />
          </Form.Group>
        </Col>
      </Row>
      <div className="mt-4">
        <Button type="submit" disabled={creating || uploadingImage}>{creating ? 'Erstellen...' : 'Modul erstellen'}</Button>
        {creating && (
          <span className="ms-2"><Spinner animation="border" size="sm" /></span>
        )}
      </div>
    </Form>
  );
}
